import { useState, useMemo } from "react";
import { Calculator, Syringe, FlaskConical, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOfflineData } from "@/contexts/OfflineDataContext";
import SyringeVisual from "@/components/protocol/SyringeVisual";

// Common EMS drug concentrations for quick selection
const COMMON_CONCENTRATIONS = [
  { drug: "Epinephrine 1:1,000", concentration: 1, unit: "mg/mL" },
  { drug: "Epinephrine 1:10,000", concentration: 0.1, unit: "mg/mL" },
  { drug: "Morphine", concentration: 10, unit: "mg/mL" },
  { drug: "Midazolam", concentration: 5, unit: "mg/mL" },
  { drug: "Fentanyl", concentration: 50, unit: "mcg/mL" },
  { drug: "Ketamine", concentration: 50, unit: "mg/mL" },
  { drug: "Ketorolac", concentration: 30, unit: "mg/mL" },
  { drug: "Ondansetron", concentration: 2, unit: "mg/mL" },
  { drug: "Dimenhydrinate", concentration: 50, unit: "mg/mL" },
  { drug: "Diphenhydramine", concentration: 50, unit: "mg/mL" },
  { drug: "Amiodarone", concentration: 50, unit: "mg/mL" },
  { drug: "Adenosine", concentration: 3, unit: "mg/mL" },
  { drug: "Atropine", concentration: 0.4, unit: "mg/mL" },
  { drug: "Glucagon", concentration: 1, unit: "mg/mL" },
  { drug: "Naloxone", concentration: 0.4, unit: "mg/mL" },
  { drug: "Dexamethasone", concentration: 4, unit: "mg/mL" },
];

export default function DrugCalculator() {
  const [weight, setWeight] = useState("");
  const { drugs } = useOfflineData();
  const [selectedDrug, setSelectedDrug] = useState(null);
  const [tab, setTab] = useState("volume"); // "volume" | "weight"
  const [drugSearch, setDrugSearch] = useState("");

  // Filtered drugs by search query (matches name, class, indications)
  const filteredDrugs = useMemo(() => {
    const q = drugSearch.trim().toLowerCase();
    if (!q) return drugs;
    return drugs.filter((d) => {
      const haystack = [
        d.name,
        d.class_name,
        ...(d.indications || []),
        ...(d.notes || []),
      ].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [drugs, drugSearch]);

  // Volume calculator state
  const [doseOrdered, setDoseOrdered] = useState("");
  const [concentration, setConcentration] = useState("");
  const [doseUnit, setDoseUnit] = useState("mg");
  const [concUnit, setConcUnit] = useState("mg/mL");
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [presetSearch, setPresetSearch] = useState("");

  // Filter Quick-Select chips by search term (matches drug name)
  const filteredPresets = useMemo(() => {
    const q = presetSearch.trim().toLowerCase();
    if (!q) return COMMON_CONCENTRATIONS;
    return COMMON_CONCENTRATIONS.filter((p) =>
      p.drug.toLowerCase().includes(q)
    );
  }, [presetSearch]);

  const volumeResult = (() => {
    const dose = parseFloat(doseOrdered);
    const conc = parseFloat(concentration);
    if (!dose || !conc || conc === 0) return null;
    // Normalize units: if dose is mcg and conc is mg/mL, convert dose to mg
    let normalizedDose = dose;
    if (doseUnit === "mcg" && concUnit === "mg/mL") {
      normalizedDose = dose / 1000;
    } else if (doseUnit === "mg" && concUnit === "mcg/mL") {
      normalizedDose = dose * 1000;
    }
    return normalizedDose / conc;
  })();

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    setConcentration(preset.concentration.toString());
    setConcUnit(preset.unit);
    setDoseUnit(preset.unit.split("/")[0]);
  };

  const calculateDose = (doseString) => {
    if (!weight || !doseString) return null;
    const match = doseString.match(/(\d+(?:\.\d+)?)\s*(?:mg|mcg)\/kg/i);
    if (match) {
      const perKg = parseFloat(match[1]);
      const calculated = perKg * parseFloat(weight);
      return calculated.toFixed(2);
    }
    return null;
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="drug-calculator-page">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Calculator className="w-6 h-6" />
          Drug Calculator
        </h1>
        <p className="text-muted-foreground">Volume & weight-based dosing</p>
      </div>

      {/* Tab toggle */}
      <div className="flex gap-2">
        <Button
          variant={tab === "volume" ? "default" : "outline"}
          onClick={() => setTab("volume")}
          className="gap-2 flex-1"
          data-testid="tab-volume"
        >
          <FlaskConical className="w-4 h-4" />
          Volume Calculator
        </Button>
        <Button
          variant={tab === "weight" ? "default" : "outline"}
          onClick={() => setTab("weight")}
          className="gap-2 flex-1"
          data-testid="tab-weight"
        >
          <Calculator className="w-4 h-4" />
          Weight-Based Dosing
        </Button>
      </div>

      {tab === "volume" ? (
        <div className="space-y-4">
          {/* Quick drug presets */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Quick Select — Common Concentrations</CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Type drug name to filter (e.g., Ketamine, Epi)…"
                  value={presetSearch}
                  onChange={(e) => setPresetSearch(e.target.value)}
                  className="pl-9 h-9 text-sm"
                  data-testid="preset-search"
                />
                {presetSearch && (
                  <button
                    onClick={() => setPresetSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
                    data-testid="preset-search-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
              {filteredPresets.length === 0 ? (
                <p className="text-xs text-muted-foreground italic px-1 py-2" data-testid="preset-search-empty">
                  No preset matches "{presetSearch}". Enter the concentration manually below.
                </p>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="preset-chips">
                  {filteredPresets.map((preset) => (
                    <Button
                      key={preset.drug}
                      variant={selectedPreset?.drug === preset.drug ? "default" : "outline"}
                      size="sm"
                      className="text-xs h-7"
                      onClick={() => handlePresetSelect(preset)}
                      data-testid={`preset-${preset.drug.toLowerCase().replace(/[\s/:,]+/g, '-')}`}
                    >
                      {preset.drug} ({preset.concentration}{preset.unit})
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Calculator inputs */}
          <Card className="border-primary/30">
            <CardContent className="pt-6 space-y-5">
              {/* Dose Ordered */}
              <div>
                <label className="text-sm font-semibold block mb-1">Dose Ordered</label>
                <p className="text-xs text-muted-foreground mb-2">The amount your provider ordered (e.g., enter '80' for 80mg)</p>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="0"
                    value={doseOrdered}
                    onChange={(e) => setDoseOrdered(e.target.value)}
                    className="font-mono text-lg h-12 flex-1"
                    data-testid="dose-ordered-input"
                  />
                  <select
                    value={doseUnit}
                    onChange={(e) => setDoseUnit(e.target.value)}
                    className="h-12 px-3 rounded-md border bg-background font-mono text-sm"
                    data-testid="dose-unit-select"
                  >
                    <option value="mg">mg</option>
                    <option value="mcg">mcg</option>
                    <option value="units">units</option>
                  </select>
                </div>
              </div>

              {/* Concentration */}
              <div>
                <label className="text-sm font-semibold block mb-1">Concentration</label>
                <p className="text-xs text-muted-foreground mb-2">Drug concentration (e.g., enter '200' for 200mg/mL)</p>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="0"
                    value={concentration}
                    onChange={(e) => { setConcentration(e.target.value); setSelectedPreset(null); }}
                    className="font-mono text-lg h-12 flex-1"
                    data-testid="concentration-input"
                  />
                  <select
                    value={concUnit}
                    onChange={(e) => setConcUnit(e.target.value)}
                    className="h-12 px-3 rounded-md border bg-background font-mono text-sm"
                    data-testid="conc-unit-select"
                  >
                    <option value="mg/mL">mg/mL</option>
                    <option value="mcg/mL">mcg/mL</option>
                    <option value="units/mL">units/mL</option>
                  </select>
                </div>
              </div>

              {/* Result */}
              {volumeResult !== null && volumeResult > 0 && (
                <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-5" data-testid="volume-result">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Volume to Draw</span>
                      <p className="text-xs text-muted-foreground">(Fill syringe to this amount in mL)</p>
                    </div>
                    <div className="text-right">
                      <span className="text-4xl font-bold font-mono text-primary" data-testid="volume-value">
                        {volumeResult.toFixed(2)}
                      </span>
                      <span className="text-lg text-muted-foreground ml-1">mL</span>
                    </div>
                  </div>

                  {selectedPreset && (
                    <div className="mt-2 text-xs text-muted-foreground">
                      {selectedPreset.drug}: {doseOrdered} {doseUnit} / {concentration} {concUnit} = {volumeResult.toFixed(2)} mL
                    </div>
                  )}

                  {/* Syringe Visual */}
                  <SyringeVisual volume={volumeResult} />
                </div>
              )}

              {volumeResult !== null && volumeResult <= 0 && (
                <p className="text-sm text-destructive">Invalid calculation — check your inputs.</p>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Weight-Based Dosing Tab - existing functionality */
        <div className="space-y-4">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Patient Weight (kg)</label>
                <Input
                  type="number"
                  placeholder="Enter weight in kg"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="font-mono text-2xl h-14"
                  data-testid="weight-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Select Drug</label>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search drug name, class, or indication..."
                    value={drugSearch}
                    onChange={(e) => setDrugSearch(e.target.value)}
                    className="pl-9 h-10"
                    data-testid="drug-search"
                  />
                  {drugSearch && (
                    <button
                      onClick={() => setDrugSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
                      data-testid="drug-search-clear"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {filteredDrugs.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4" data-testid="drug-search-empty">
                    No drugs match "{drugSearch}"
                  </p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-testid="drug-grid">
                    {filteredDrugs.map(drug => (
                      <Button
                        key={drug.id}
                        variant={selectedDrug?.id === drug.id ? "default" : "outline"}
                        onClick={() => setSelectedDrug(drug)}
                        className="justify-start text-left text-sm"
                        data-testid={`drug-btn-${drug.id}`}
                      >
                        {drug.name}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {selectedDrug && (
            <Card className="border-primary">
              <CardHeader>
                <CardTitle className="font-heading">{selectedDrug.name}</CardTitle>
                <Badge variant="outline">{selectedDrug.class_name}</Badge>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-muted-foreground">Adult Dose</span>
                    <p className="font-mono">{selectedDrug.dose_adult}</p>
                  </div>
                  {selectedDrug.dose_pediatric && (
                    <div>
                      <span className="text-sm text-muted-foreground">Pediatric Dose</span>
                      <p className="font-mono">{selectedDrug.dose_pediatric}</p>
                    </div>
                  )}
                </div>

                {weight && calculateDose(selectedDrug.dose_pediatric || selectedDrug.dose_adult) && (
                  <div className="p-4 bg-primary/10 rounded-lg">
                    <span className="text-sm text-muted-foreground">Calculated for {weight} kg</span>
                    <p className="font-mono text-3xl font-bold text-primary">
                      {calculateDose(selectedDrug.dose_pediatric || selectedDrug.dose_adult)} mg
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <div>
                    <span className="text-sm text-muted-foreground">Route</span>
                    <div className="flex gap-1 mt-1">
                      {selectedDrug.route.map(r => (
                        <Badge key={r} variant="secondary">{r}</Badge>
                      ))}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Indications</span>
                    <ul className="list-disc list-inside text-sm mt-1">
                      {selectedDrug.indications.map((ind, i) => (
                        <li key={i}>{ind}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Contraindications</span>
                    <ul className="list-disc list-inside text-sm mt-1 text-destructive">
                      {selectedDrug.contraindications.map((con, i) => (
                        <li key={i}>{con}</li>
                      ))}
                    </ul>
                  </div>
                  {selectedDrug.notes && selectedDrug.notes.length > 0 && (
                    <div>
                      <span className="text-sm text-muted-foreground">Notes</span>
                      <ul className="list-disc list-inside text-sm mt-1">
                        {selectedDrug.notes.map((note, i) => (
                          <li key={i}>{note}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
